import React, { FC, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { EOrganizationSelectionStatus, IOrganizationContext, OrganizationContext } from './OrganizationContext';
import { IBasicOrganization } from '../resources/models/IOrganization';
import { CurrentUserContext } from './CurrentUserContext';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { AuthContext } from './AuthContext';
import { OrganizationFetcher } from '../components/Customer/CustomerFetchers';
import { LazyOrganizationContext } from './LazyOrganizationContext';
import { UUID } from '../types/uuid';

const ORGANIZATION_SELECTION_LOCAL_STORAGE_KEY = 'currentOrganizationSelection';

interface IOrganizationSelection {
  organizationId: string | null;
  selectionStatus: EOrganizationSelectionStatus;
}

export const OrganizationContextProvider: FC = ({ children }) => {
  const { identity } = useContext(AuthContext);
  const { userInfo } = useContext(CurrentUserContext);

  const [organizationSelection, setOrganizationSelection] = useLocalStorage<IOrganizationSelection>(
    ORGANIZATION_SELECTION_LOCAL_STORAGE_KEY,
    {
      organizationId: null,
      selectionStatus: EOrganizationSelectionStatus.NotSelected,
    }
  );

  const { organizationId } = organizationSelection;

  const organization = useMemo(() => {
    return userInfo?.organizations?.find((organization) => organization.id === organizationId) ?? null;
  }, [organizationId, userInfo?.organizations]);

  const setOrganization = useCallback((organization: IBasicOrganization | null) => {
    if (organization) {
      setOrganizationSelection({
        organizationId: organization.id,
        selectionStatus: EOrganizationSelectionStatus.Selected,
      });
    } else {
      setOrganizationSelection({
        organizationId: null,
        selectionStatus: EOrganizationSelectionStatus.Skipped,
      });
    }
  }, []);

  useEffect(() => {
    if (!identity) {
      setOrganizationSelection({
        organizationId: null,
        selectionStatus: EOrganizationSelectionStatus.NotSelected,
      });
    }
    // switching identities
    if (identity) {
      return () => {
        setOrganizationSelection({
          organizationId: null,
          selectionStatus: EOrganizationSelectionStatus.NotSelected,
        });
      };
    }
  }, [identity?.userId]);

  const prevUserInfo = useRef(userInfo);

  useEffect(() => {
    if (!userInfo) return;

    /**
     * prepare the list of organization ids separated by ",", that is easy to compare so that we can check if that list changed
     */
    const normalizeOrgList = (organizations: IBasicOrganization[]) =>
      organizations
        .map((organization) => organization.id)
        .sort()
        .join(',');

    const orgListChanged =
      prevUserInfo.current &&
      normalizeOrgList(prevUserInfo.current?.organizations ?? []) !== normalizeOrgList(userInfo?.organizations ?? []);

    const userChanged = prevUserInfo.current && prevUserInfo.current?.id !== userInfo?.id;

    /**
     * if user or their list of organizations changed, the organization selection needs to be reset
     */
    if (userChanged || orgListChanged) {
      if (userInfo.default_organization) {
        setOrganizationSelection({
          organizationId: userInfo.default_organization,
          selectionStatus: EOrganizationSelectionStatus.Default,
        });
      } else {
        setOrganizationSelection({
          organizationId: null,
          selectionStatus: EOrganizationSelectionStatus.NotSelected,
        });
      }
    } else if (
      /**
       * if user with default organization has no default organization or
       * if user's selected org is not on the list of available organizations
       */
      userInfo.default_organization &&
      ((!organizationSelection.organizationId &&
        organizationSelection.selectionStatus === EOrganizationSelectionStatus.NotSelected) ||
        (userInfo.default_organization !== organizationSelection.organizationId &&
          userInfo?.organizations &&
          !userInfo.organizations.some((organization) => organization.id === organizationSelection.organizationId)))
    ) {
      setOrganizationSelection({
        organizationId: userInfo.default_organization,
        selectionStatus: EOrganizationSelectionStatus.Default,
      });
    }

    prevUserInfo.current = userInfo;
  }, [userInfo, organizationSelection]);

  const contextValue = useMemo(
    () => ({
      selectionStatus: organizationSelection.selectionStatus,
      organization,
      organizationId,
      setOrganization,
    }),
    [organization, organizationSelection.selectionStatus]
  );

  return <OrganizationContext.Provider value={contextValue}>{children}</OrganizationContext.Provider>;
};

export const OrganizationContextProviderFromFetcher: FC = ({ children }) => {
  const { data, isLoading } = useContext(OrganizationFetcher.Context);

  const ctxValue = useMemo(() => {
    return {
      selectionStatus: EOrganizationSelectionStatus.Injected,
      organization: data?.payload ?? null,
      organizationId: data?.payload.id ?? null,
      setOrganization: () => {
        throw new Error('cannot be called in this context');
      },
    };
  }, [data?.payload]);

  return isLoading ? null : <OrganizationContext.Provider value={ctxValue}>{children}</OrganizationContext.Provider>;
};

/**
 * @description Use it to turn Selected or Default organization context into Injected one
 */
export const OrganizationContextProviderFromSelf: FC = ({ children }) => {
  const { organization } = useContext(OrganizationContext);

  const ctxValue = useMemo(() => {
    return {
      selectionStatus: organization ? EOrganizationSelectionStatus.Injected : EOrganizationSelectionStatus.NotSelected,
      organization: organization,
      organizationId: organization?.id ?? null,
      setOrganization: () => {
        throw new Error('cannot be called in this context');
      },
    };
  }, [organization]);

  return <OrganizationContext.Provider value={ctxValue}>{children}</OrganizationContext.Provider>;
};

export const OrganizationContextProviderLazy: FC<{ id: UUID }> = ({ id, children }) => {
  const { data, requestData } = useContext(LazyOrganizationContext);

  const lcId = id?.toLowerCase();

  useEffect(() => {
    requestData(lcId);
  }, [lcId, requestData]);

  const org = data[lcId];
  const ctxValue = useMemo<IOrganizationContext>(() => {
    return {
      selectionStatus: EOrganizationSelectionStatus.Injected,
      /**
       * The `organization` property does not meet the full `IBasicOrganization` interface, but we usually don't need
       * anything more than `id` and `name` so it's fine.
       */
      organization: (org as IBasicOrganization) ?? null,
      organizationId: lcId,
      setOrganization: () => {
        throw new Error('cannot be called in this context');
      },
    };
  }, [org, lcId]);

  return <OrganizationContext.Provider value={ctxValue}>{children}</OrganizationContext.Provider>;
};

export const NoOrganizationContextProvider: FC = ({ children }) => {
  const ctxValue = useMemo<IOrganizationContext>(() => {
    return {
      selectionStatus: EOrganizationSelectionStatus.Injected,
      organization: null,
      organizationId: null,
      setOrganization: () => {
        throw new Error('cannot be called in this context');
      },
    };
  }, []);

  return <OrganizationContext.Provider value={ctxValue}>{children}</OrganizationContext.Provider>;
};
